import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Copy, ExternalLink, MapPin, Maximize2, MoreHorizontal, Pencil, Tag, Users, Video, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { formatInZone } from '../../lib/timezone';
import { addDaysKey, allDayRange, daysBetween, longDayLabel } from '../../lib/calendar/zonedCalendar';
import { eventCapabilities, KIND_LABELS, SOURCE_LABELS } from '../../lib/calendar/eventCapabilities';
import { guestDeclinedText } from '../../lib/bookingStatus';
import { DestructiveEventDialog } from './DestructiveEventDialog';
import { EventTagPill } from './EventTagPill';

export const secondaryBtn = 'inline-flex items-center gap-[7px] h-8 px-[13px] border border-[#E4E1DB] rounded-[9px] bg-white text-[12.5px] font-medium text-[#3A3532] hover:bg-[#F4F2EE]';
export const primaryBtn = 'inline-flex items-center gap-[7px] h-8 px-[13px] rounded-[9px] bg-[#4D11F6] text-white text-[12.5px] font-semibold hover:bg-[#3B0DBE]';
export const dangerBtn = 'inline-flex items-center gap-[7px] h-8 px-[13px] border border-[#F3D3D3] rounded-[9px] bg-white text-[12.5px] font-medium text-[#A3382B] hover:bg-[#FEF2F2]';

/** "Thursday, 17 September 2026 · 9:30 AM – 10 AM" in the display zone. */
export function whenLabel(event, zone) {
    if (event.all_day) {
        // A calendar date, never zone-shifted (zonedCalendar's module note).
        const range = allDayRange(event, zone);
        if (range) {
            const first = longDayLabel(range[0]);
            if (daysBetween(range[0], range[1]) <= 1) return `${first} · All day`;
            return `${first} – ${longDayLabel(addDaysKey(range[1], -1))} · All day`;
        }
    }
    const day = formatInZone(event.start, { zone, formatStr: 'EEEE, d MMMM yyyy' });
    if (event.all_day) return `${day} · All day`;
    const t = (d) => formatInZone(d, { zone, formatStr: 'h:mm a' }).replace(':00 ', ' ');
    return `${day} · ${t(event.start)} – ${t(event.end)}`;
}

/**
 * The quick view a chip opens (#680, prototype `popOpen`): a 440px card with
 * the essentials and the actions; the expand icon (or ⋯ › Open full details)
 * goes to the full event page. A Radix Dialog, not a hand-rolled fixed
 * overlay — the shell forbids those, and every other dialog in the app stacks
 * the same way.
 *
 * Layout (the design's card): title with expand + close on ONE row (the
 * primitive's own absolutely-placed X is hidden so the two icons align);
 * Join; then icon rows — when, where, who, tags — each icon on its row's
 * first line; then Edit · the destructive action · ⋯. The design's "Chat"
 * button has no backend and is omitted (#680 D13). Tags are read-only here:
 * they are edited on the full event page and in the editor (#680).
 *
 * Replaces EventDetailModal's detail half; editing moved to the editor page.
 */
export function EventQuickView({ event, displayZone, dayKey, onClose, viewerEmail = '' }) {
    const [confirming, setConfirming] = useState(false);
    if (!event) return null;
    const caps = eventCapabilities(event);
    const detailsHref = `/calendar/event/${encodeURIComponent(event.event_id)}?day=${dayKey}`;
    const editHref = `/calendar/event/${encodeURIComponent(event.event_id)}/edit?day=${dayKey}`;
    const attendees = (event.attendees || []).filter(a => a && (a.email || a.name));
    const tags = (event.tags || []).filter(t => t && t.name);
    const organizer = event.organizer_email || '';
    const youOrganize = Boolean(organizer && viewerEmail && organizer.toLowerCase() === viewerEmail.toLowerCase());
    const whenSub = [event.is_recurring_instance && 'Recurring · part of a series', `Times in ${displayZone}`].filter(Boolean).join(' · ');
    const kindSource = [KIND_LABELS[event.kind] || event.kind, SOURCE_LABELS[event.source]].filter(Boolean).join(' · ');

    return (
        <>
            <Dialog open={!!event && !confirming} onOpenChange={(open) => { if (!open) onClose(); }}>
                <DialogContent
                    hideClose
                    className="top-[96px] translate-y-0 data-[state=open]:slide-in-from-top-2 max-w-[440px] w-[calc(100vw-32px)] p-0 gap-0 rounded-[16px] border-[#E9E7E2] shadow-[0_18px_44px_rgba(12,10,9,.18)] overflow-hidden"
                    data-testid="event-detail-modal"
                >
                    <div className="px-5 pt-[18px] pb-5">
                        <div className="flex items-start gap-1.5">
                            <div className="flex-1 min-w-0 pt-[3px]">
                                <DialogTitle className={`text-[17px] font-semibold leading-[1.35] tracking-[-0.01em] text-[#0C0A09] [text-wrap:pretty] ${event.status === 'cancelled' ? 'line-through' : ''}`}>
                                    {event.title || '(No title)'}
                                </DialogTitle>
                                <p className="mt-0.5 text-[12px] text-[#A29B95]" data-testid="event-kind-source">{kindSource}</p>
                            </div>
                            <Link
                                to={detailsHref}
                                state={{ event }}
                                title="Open full details"
                                aria-label="Open full details"
                                className={iconBtn}
                                data-testid="event-open-full"
                            >
                                <Maximize2 className="w-4 h-4" />
                            </Link>
                            <DialogClose className={iconBtn} aria-label="Close" title="Close" data-testid="event-quick-close">
                                <XIcon className="w-[17px] h-[17px]" />
                            </DialogClose>
                        </div>
                        <DialogDescription className="sr-only">
                            {`${KIND_LABELS[event.kind] || event.kind} from ${SOURCE_LABELS[event.source] || 'a calendar'}`}
                        </DialogDescription>

                        <GuestDeclinedNote event={event} className="mt-3 p-2.5 rounded-[10px] text-[12.5px] leading-relaxed" />

                        {event.conference_link && (
                            <div className="flex items-center gap-2 mt-4">
                                <a href={event.conference_link} target="_blank" rel="noopener noreferrer" className={qvPrimary} data-testid="join-meeting-link">
                                    <Video className="w-4 h-4" /> Join
                                </a>
                            </div>
                        )}

                        <div className="h-px bg-[#EDEBE6] mt-4 mb-4" />
                        <div className="flex flex-col gap-3.5">
                            <Row icon={Clock}>
                                <div className="text-[14px] leading-[21px] text-[#0C0A09]">{whenLabel(event, displayZone)}</div>
                                <div className="text-[12.5px] text-[#8A837E] mt-0.5">{whenSub}</div>
                            </Row>
                            {event.location && (
                                <Row icon={MapPin}><div className="text-[14px] leading-[21px] text-[#3A3532] break-words">{event.location}</div></Row>
                            )}
                            {(organizer || attendees.length > 0) && (
                                <Row icon={Users}>
                                    <div className="text-[14px] leading-[21px] text-[#0C0A09] break-all" data-testid="event-organizer">
                                        {youOrganize ? "You're the organizer" : organizer || `${attendees.length} attendee${attendees.length === 1 ? '' : 's'}`}
                                    </div>
                                    {attendees.length > 0 && (
                                        <div className="text-[12.5px] text-[#8A837E] mt-0.5 truncate">
                                            {attendees.slice(0, 3).map(a => a.name || a.email).join(', ')}{attendees.length > 3 ? ` +${attendees.length - 3}` : ''}
                                        </div>
                                    )}
                                </Row>
                            )}
                            <Row icon={Tag} label="Tags" box="h-6" nudge="">
                                {tags.length ? (
                                    <div className="flex flex-wrap gap-1.5" data-testid="event-tags">
                                        {tags.map(t => <EventTagPill key={t.tag_id} tag={t} />)}
                                    </div>
                                ) : (
                                    <div className="text-[13px] text-[#A29B95] leading-6" data-testid="event-tags">No tags</div>
                                )}
                            </Row>
                        </div>

                        {(caps.readOnlyReason || caps.note) && (
                            <p
                                className="mt-4 p-2.5 rounded-[10px] bg-[#FBFAF9] border border-[#EDEBE6] text-[12px] text-[#57504B] leading-relaxed"
                                data-testid={event.source === 'booking_link' ? 'booking-no-calendar-note' : 'event-capability-note'}
                            >
                                {caps.readOnlyReason || caps.note}
                            </p>
                        )}

                        <div className="flex items-center gap-2 mt-5">
                            {caps.canEdit && (
                                <Link to={editHref} state={{ event }} className={qvSecondary} data-testid="edit-event-btn">
                                    <Pencil className="w-4 h-4 text-[#57504B]" /> Edit
                                </Link>
                            )}
                            {caps.canCancelBooking && (
                                <button type="button" onClick={() => setConfirming(true)} className={qvDanger} data-testid="cancel-booking-btn">
                                    <XIcon className="w-4 h-4" /> Cancel booking
                                </button>
                            )}
                            {caps.canDelete && (
                                <button type="button" onClick={() => setConfirming(true)} className={qvDanger} data-testid="delete-event-btn">
                                    <XIcon className="w-4 h-4" /> Delete
                                </button>
                            )}
                            <div className="flex-1" />
                            <DropdownMenu modal={false}>
                                <DropdownMenuTrigger asChild>
                                    <button type="button" className={iconBtn} aria-label="More actions" title="More actions" data-testid="event-more-actions">
                                        <MoreHorizontal className="w-[18px] h-[18px]" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="min-w-[200px]">
                                    <DropdownMenuItem asChild>
                                        <Link to={detailsHref} state={{ event }} data-testid="event-menu-full-details">
                                            <Maximize2 className="w-4 h-4 mr-2" /> Open full details
                                        </Link>
                                    </DropdownMenuItem>
                                    {event.booking_link_slug && (
                                        <>
                                            <DropdownMenuItem onSelect={() => window.open(`/book/${event.booking_link_slug}`, '_blank', 'noopener')} data-testid="open-booking-page">
                                                <ExternalLink className="w-4 h-4 mr-2" /> Open booking page
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onSelect={() => copyBookingLink(event.booking_link_slug)} data-testid="copy-booking-link">
                                                <Copy className="w-4 h-4 mr-2" /> Copy booking link
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    {event.html_link && (
                                        <DropdownMenuItem onSelect={() => window.open(event.html_link, '_blank', 'noopener')} data-testid="event-open-provider">
                                            <ExternalLink className="w-4 h-4 mr-2" /> Open in {providerLinkLabel(event.html_link)}
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <DestructiveEventDialog
                event={confirming ? event : null}
                onCancel={() => setConfirming(false)}
                onDone={() => { setConfirming(false); onClose(); }}
            />
        </>
    );
}

const iconBtn = 'flex-none w-8 h-8 rounded-lg flex items-center justify-center text-[#57504B] hover:bg-[#F4F2EE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4D11F6]/30';
const qvPrimary = 'inline-flex items-center gap-2 h-9 px-4 rounded-[10px] bg-[#4D11F6] text-white text-[13.5px] font-semibold hover:bg-[#3B0DBE]';
const qvSecondary = 'inline-flex items-center gap-2 h-9 px-3.5 border border-[#E4E1DB] rounded-[10px] bg-white text-[13.5px] font-medium text-[#3A3532] hover:bg-[#F4F2EE]';
const qvDanger = 'inline-flex items-center gap-2 h-9 px-3.5 border border-[#F3D3D3] rounded-[10px] bg-white text-[13.5px] font-medium text-[#A3382B] hover:bg-[#FEF2F2]';

/**
 * Which calendar an event's `html_link` opens, named from the link itself.
 * Not from `event.source`: a CRM meeting or a booking carries its Google /
 * Outlook twin's link (calendar_aggregator backfills it), so "Open in
 * NurtureCRM" would be wrong.
 */
export function providerLinkLabel(url) {
    try {
        const host = new URL(url).hostname;
        if (/(^|\.)google\.com$/.test(host)) return 'Google Calendar';
        if (/(^|\.)(outlook\.(com|live\.com|office\.com|office365\.com)|office\.com|live\.com)$/.test(host)) return 'Outlook';
    } catch { /* not a URL: fall through */ }
    return 'calendar';
}

function copyBookingLink(slug) {
    navigator.clipboard.writeText(`${window.location.origin}/book/${slug}`);
    toast.success('Link copied to clipboard');
}

/**
 * One icon row. The icon is centred in a box exactly as tall as the row's
 * FIRST line (21px = the 14px text's line-height; 24px = a tag pill), so it
 * stays on that line however many lines follow. On text rows it then drops
 * 1px: centring on the line box centres it on the capitals, while the eye
 * reads a mostly-lowercase line by its x-height, which sits ~1.5px lower in
 * Inter. A pill is a filled box, so the tag icon centres on the box itself.
 */
function Row({ icon: Icon, label, box = 'h-[21px]', nudge = 'translate-y-px', children }) {
    return (
        <div className="flex items-start gap-3">
            <span className={`flex-none flex items-center ${box}`} data-testid="event-row-icon">
                <Icon className={`w-[17px] h-[17px] text-[#57504B] ${nudge}`} aria-hidden={label ? undefined : 'true'} aria-label={label} role={label ? 'img' : undefined} />
            </span>
            <div className="flex-1 min-w-0">{children}</div>
        </div>
    );
}

/**
 * The amber notice for a booking whose guest declined the calendar invite (#791
 * D1): the booking stays confirmed and the time stays held, so the host decides
 * whether to cancel. One component and one `guestDeclinedText` for the quick
 * view and the full event page, so both read the same. Renders nothing for any
 * other event. `guest_name` rides on the `bl_` chip and on the provider twin;
 * without it the copy says "The guest".
 */
export function GuestDeclinedNote({ event, className = '' }) {
    const text = guestDeclinedText(event);
    if (!text) return null;
    return (
        <p role="status" className={`bg-amber-50 border border-amber-200 text-amber-800 ${className}`} data-testid="booking-guest-declined-note">
            {text}
        </p>
    );
}

export function BookingLinkActions({ slug }) {
    return (
        <>
            <a href={`/book/${slug}`} target="_blank" rel="noopener noreferrer" className={secondaryBtn} data-testid="open-booking-page" title="Open booking page">
                <ExternalLink className="w-3.5 h-3.5" /> Booking page
            </a>
            <button
                type="button"
                onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/book/${slug}`);
                    toast.success('Link copied to clipboard');
                }}
                className={secondaryBtn}
                data-testid="copy-booking-link"
                title="Copy booking link"
            >
                <Copy className="w-3.5 h-3.5" /> Copy link
            </button>
        </>
    );
}
